/**
 * lib/env/environment.ts — IDENTIDAD de entorno y CAPABILITY analítica (fuente: servidor).
 *
 * Correctivo Codex:
 *   · No basta el puerto: se valida protocolo + hostname permitido + puerto + environment_id
 *     REAL del servidor (runtime_environment_info, migr 00227/00228, SÓLO en QA). El PRINCIPAL
 *     es pristino 00210: carece del RPC, y su AUSENCIA (PGRST202) es el marcador de identidad.
 *   · analytics_available lo calcula el servidor comprobando la existencia y firma de las 4
 *     RPC analíticas; el cliente además exige payload completo y ledger mínimo compatible.
 *   · Nunca se imprimen URLs completas ni parámetros sensibles.
 */

import { supabase } from '@lib/supabase/client'
import { marcarServidorAlcanzable } from '@lib/net/reachability'

export type EnvKind = 'qa' | 'principal' | 'staging' | 'production' | 'unknown'
/** environment_id declarado por cada base (COMMENT ON DATABASE ... 'env:<id>' o app.environment_id). */
export type EnvironmentId =
  | 'qa_local'
  | 'principal_local'
  | 'staging_cloud'
  | 'production_cloud'
  | 'unknown'

export interface EnvInfo {
  kind: EnvKind
  label: string
  color: 'amber' | 'red' | 'gray'
  /** Sólo host:puerto — nunca la URL completa con claves/parámetros. */
  origin: string
  host: string
  port: string
  protocol: string
  declaredEnv: string | null
  /** Sólo referencia de configuración; NO se muestra como ledger real. */
  expectedLedger: string | null
}

/** Ledger mínimo que el frontend nuevo requiere para usar la analítica. */
export const MIN_ANALYTICS_LEDGER = '00211'
const ALLOWED_HOSTS = ['127.0.0.1', 'localhost']
/** Nube (CLOUD-11/24): sólo https://<ref de 20 caracteres>.supabase.co, y sólo declarando staging/production. */
const CLOUD_HOST = /^[a-z0-9]{20}\.supabase\.co$/
const CLOUD_KINDS: ReadonlyArray<EnvKind> = ['staging', 'production']
const EXPECTED_ID: Record<Exclude<EnvKind, 'unknown'>, EnvironmentId> = {
  qa: 'qa_local',
  principal: 'principal_local',
  staging: 'staging_cloud',
  production: 'production_cloud',
}
const LABEL: Record<EnvKind, string> = {
  qa: 'QA',
  principal: 'PRINCIPAL',
  staging: 'STAGING',
  production: 'PRODUCCIÓN',
  unknown: 'DESCONOCIDO',
}

export type AnalyticsCapability =
  | { status: 'loading' }
  | { status: 'available'; ledger: string; environmentId: EnvironmentId }
  // 'unavailable' = el RPC de identidad RESPONDIÓ (runtime_environment_info existe) pero la
  // ANALÍTICA (00211+) no está desplegada. Trae identidad y ledger reales.
  | { status: 'unavailable'; ledger: string; environmentId: EnvironmentId }
  | { status: 'incompatible'; ledger: string; environmentId: EnvironmentId } // ledger viejo
  // 'absent' = el propio runtime_environment_info NO existe (PGRST202). Es el marcador del
  // principal pristino 00210. Se distingue de 'unavailable' para el bloqueo (compat temporal).
  | { status: 'absent' }
  | { status: 'forbidden' }
  | { status: 'error'; message: string } // incluye payload malformado
  | { status: 'offline' }

/** Motivo por el que la app debe BLOQUEARSE por completo (no sólo avisar). */
export type EnvBlockReason =
  | { kind: 'cross-env'; detail: string }
  | { kind: 'host'; detail: string }
  | { kind: 'unverified'; detail: string }

// ── Entorno declarado por el build (runtime) ─────────────────────────────────
export function detectEnvironment(): EnvInfo {
  const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) ?? ''
  const declaredEnv = (import.meta.env.VITE_APP_ENV as string | undefined) ?? null
  const expectedLedger = (import.meta.env.VITE_EXPECTED_LEDGER as string | undefined) ?? null

  let host = ''
  let port = ''
  let protocol = ''
  try {
    const u = new URL(supabaseUrl)
    host = u.hostname
    protocol = u.protocol.replace(':', '')
    port = u.port || (protocol === 'https' ? '443' : '80')
  } catch {
    /* URL inválida → queda todo vacío y la validación lo bloquea */
  }

  let kind: EnvKind = 'unknown'
  if (port === '55321') kind = 'qa'
  else if (port === '54321') kind = 'principal'
  else if (declaredEnv && declaredEnv in EXPECTED_ID) kind = declaredEnv as EnvKind

  return {
    kind,
    label: LABEL[kind],
    color: kind === 'qa' || kind === 'staging' ? 'amber' : kind === 'unknown' ? 'gray' : 'red',
    origin: host ? `${host}:${port}` : '(sin configurar)',
    host,
    port,
    protocol,
    declaredEnv,
    expectedLedger,
  }
}

/** Validación estática: protocolo, hostname permitido y puerto vs modo declarado. */
export function crossEnvViolation(env: EnvInfo): string | null {
  if (!env.host) return 'VITE_SUPABASE_URL ausente o inválida.'
  if (isCloudHost(env.host) || CLOUD_KINDS.includes(env.kind)) {
    // Nube: https + host <ref>.supabase.co + modo staging/production declarado. Nunca un host
    // de nube con modo local (qa/principal) ni un modo de nube contra un host local.
    if (env.protocol !== 'https')
      return `La nube exige https (recibido: ${env.protocol || '(vacío)'}).`
    if (!isCloudHost(env.host)) return `Host no permitido en la nube: ${env.host}.`
    if (!env.declaredEnv || !CLOUD_KINDS.includes(env.declaredEnv as EnvKind))
      return `Un destino en la nube exige VITE_APP_ENV=staging|production (declarado: ${env.declaredEnv ?? '(ninguno)'}).`
    return null
  }
  if (!ALLOWED_HOSTS.includes(env.host))
    return `Host no permitido en desarrollo: ${env.host}. Sólo ${ALLOWED_HOSTS.join(' / ')}.`
  if (env.protocol !== 'http' && env.protocol !== 'https')
    return `Protocolo no soportado: ${env.protocol || '(vacío)'}.`
  if (!env.declaredEnv) return null
  if (env.declaredEnv === 'qa' && env.port !== '55321')
    return `dev:qa declarado pero el destino es :${env.port} (se esperaba :55321 QA).`
  if (env.declaredEnv === 'principal' && env.port !== '54321')
    return `dev:principal declarado pero el destino es :${env.port} (se esperaba :54321 principal).`
  return null
}

function isCloudHost(host: string): boolean {
  return CLOUD_HOST.test(host)
}

/** Comparación de ledgers "00211" < "00227" (cadenas de igual longitud, orden natural). */
export function ledgerAtLeast(ledger: string | null, min: string): boolean {
  if (!ledger || !/^\d+$/.test(ledger)) return false
  return ledger.padStart(min.length, '0') >= min
}

// ── Sonda de identidad + capability ──────────────────────────────────────────
export async function probeRuntime(): Promise<AnalyticsCapability> {
  // El servidor contestó algo: alcanzable, sea cual sea el veredicto de identidad.
  const contesto = <T extends AnalyticsCapability>(cap: T): T => {
    marcarServidorAlcanzable(true)
    return cap
  }
  // No llegamos al servidor. NO es identidad dudosa: es no haber podido preguntar.
  const noContesto = (): AnalyticsCapability => {
    marcarServidorAlcanzable(false)
    return { status: 'offline' }
  }
  try {
    const { data, error, status } = await supabase.rpc('runtime_environment_info')
    if (error) {
      const code = (error as { code?: string }).code ?? ''
      // El RPC de identidad NO existe → marcador del principal pristino 00210.
      if (code === 'PGRST202') return contesto({ status: 'absent' })
      if (code === 'PGRST301' || code === '42501') return contesto({ status: 'forbidden' })
      // Sin código de PostgREST no hubo respuesta del API. postgrest-js sólo rellena
      // `code` cuando el error viene de PostgREST/PostgreSQL; en un fallo de red deja
      // code:'' y status:0, y ante un 502/503 de la pasarela devuelve su cuerpo, que
      // tampoco trae código. Es exactamente el caso "hay wifi pero no hay servidor":
      // se arranca sin conexión, no se bloquea la aplicación.
      if (!code || status === 0) return noContesto()
      return contesto({ status: 'error', message: error.message })
    }

    // Payload COMPLETO y válido; cualquier forma inesperada es un error, no un "ok".
    const p = data as Partial<Record<string, unknown>> | null
    const envId = typeof p?.environment_id === 'string' ? (p.environment_id as EnvironmentId) : null
    const ledger = typeof p?.ledger === 'string' ? p.ledger : null
    const avail = typeof p?.analytics_available === 'boolean' ? p.analytics_available : null
    const time = typeof p?.server_time === 'string' ? p.server_time : null
    // Ojo: un payload malformado SÍ sigue siendo un servidor que contestó y en el que no
    // se puede confiar. Eso bloquea, y debe seguir bloqueando.
    if (!envId || !ledger || avail === null || !time)
      return contesto({ status: 'error', message: 'Respuesta de entorno incompleta o malformada.' })

    if (!avail) return contesto({ status: 'unavailable', ledger, environmentId: envId })
    if (!ledgerAtLeast(ledger, MIN_ANALYTICS_LEDGER))
      return contesto({ status: 'incompatible', ledger, environmentId: envId })
    return contesto({ status: 'available', ledger, environmentId: envId })
  } catch {
    return noContesto()
  }
}

/**
 * ¿Debe bloquearse TODA la app? offline/forbidden/loading NO bloquean (el POS offline debe
 * seguir), pero tampoco declaran identidad verificada: las áreas sensibles online
 * (Dashboard/Reportes) siguen gateadas por la capability, que sólo es 'available' tras una
 * verificación exitosa.
 *
 * Distinción CLAVE (correctivo Codex):
 *   · 'absent' (PGRST202: runtime_environment_info NO existe) = marcador del PRINCIPAL
 *     pristino 00210. Es COMPATIBILIDAD TEMPORAL: no bloquea SÓLO si el modo declarado es
 *     principal. En QA la ausencia del RPC significa que NO estamos en QA → cruce → bloqueo.
 *   · RPC PRESENTE (available/unavailable/incompatible) pero environment_id='unknown' = base
 *     con la cadena canónica SIN bootstrap de identidad. NO es compatibilidad: es una
 *     identidad NO configurada y se BLOQUEA SIEMPRE (incluido principal). Un futuro principal
 *     con el RPC desplegado DEBE declarar principal_local; 'unknown' nunca se acepta.
 *   · RPC presente con identidad declarada: debe COINCIDIR con el modo solicitado.
 */
export function resolveBlock(env: EnvInfo, cap: AnalyticsCapability): EnvBlockReason | null {
  const stat = crossEnvViolation(env)
  if (stat)
    return {
      kind:
        env.host && !ALLOWED_HOSTS.includes(env.host) && !isCloudHost(env.host)
          ? 'host'
          : 'cross-env',
      detail: stat,
    }

  // RPC de identidad AUSENTE (principal 00210): compat temporal SÓLO en principal declarado.
  if (cap.status === 'absent') {
    if (env.declaredEnv === 'principal' || env.kind === 'principal') return null
    return {
      kind: 'cross-env',
      detail: `Se solicitó ${env.label} pero el servidor no expone runtime_environment_info (destino ${env.origin}).`,
    }
  }

  // Estados sin respuesta útil del servidor: no se bloquea la app (POS offline), pero la
  // identidad queda SIN verificar (las áreas sensibles siguen gateadas por capability).
  if (cap.status === 'loading' || cap.status === 'offline' || cap.status === 'forbidden')
    return null

  // El servidor respondió con forma inesperada: no se puede confiar en la identidad.
  if (cap.status === 'error')
    return {
      kind: 'unverified',
      detail: 'No se pudo verificar la identidad del entorno (respuesta inesperada).',
    }

  // A partir de aquí el RPC RESPONDIÓ (available | unavailable | incompatible).
  const realId = cap.environmentId

  // RPC PRESENTE pero SIN identidad declarada (unknown) = cadena aplicada sin bootstrap.
  // FAIL-CLOSED: se bloquea SIEMPRE — nunca es compatibilidad, ni siquiera declarando principal.
  if (realId === 'unknown') {
    return {
      kind: 'unverified',
      detail: `El servidor respondió pero no declara identidad de entorno (identidad no configurada; destino ${env.origin}).`,
    }
  }

  // Identidad presente: debe coincidir con el modo declarado.
  if (env.kind !== 'unknown' && realId !== EXPECTED_ID[env.kind]) {
    return {
      kind: 'cross-env',
      detail: `El servidor se identifica como "${realId}" pero se solicitó ${env.label}.`,
    }
  }
  return null
}
